import React, { useState } from "react";

/**
 * ProjectHealth — compact derived-health indicator.
 * Backend returns { status, score, reasons: [{code, category, text, meta}],
 * reason_text: [string], override: {status, reason}|null }.
 *
 * Props
 *   health: the object above (or null while loading)
 *   compact: true (default) shows a small dot + label; false shows a full
 *            block suitable for the top of a project detail view.
 *   hideCategories: string[] of reason `category` values to omit from the
 *                   displayed reasons (Team Access uses this to hide budget).
 */
const COLORS = {
  "On Track": "#10B981",
  "Needs Attention": "#F59E0B",
  "At Risk": "#EF4444",
  "Overdue": "#EF4444",
};

function statusColor(s) { return COLORS[s] || "#94A3B8"; }

export default function ProjectHealth({ health, compact = true, hideCategories = [], testid }) {
  const [showAll, setShowAll] = useState(false);
  if (!health) return null;
  const effective = health.override?.status || health.status;
  const color = statusColor(effective);
  const filtered = (health.reasons || []).filter(r => !hideCategories.includes(r.category));
  const previewCount = compact ? 2 : 5;
  const previewText = filtered.slice(0, previewCount).map(r => r.text).join(" · ");

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/85 whitespace-nowrap"
        title={filtered.map(r => r.text).join(" · ")}
        data-testid={testid || "project-health-badge"}
        data-health-status={effective}
      >
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}55` }}/>
        <span>{effective}</span>
        {health.override?.status && (
          <span className="text-[9px] text-white/40 normal-case tracking-normal" title={`Auto: ${health.status} · ${health.override.reason || ""}`}>(override)</span>
        )}
      </span>
    );
  }

  const visible = showAll ? filtered : filtered.slice(0, previewCount);
  return (
    <div className="p-3 rounded-lg border" style={{ borderColor: `${color}55`, backgroundColor: `${color}0d` }} data-testid={testid || "project-health-block"} data-health-status={effective}>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}55` }}/>
        <div className="text-sm font-semibold" style={{ color }}>{effective}</div>
        {health.override?.status && (
          <span className="text-[10px] uppercase tracking-wider text-white/50">
            override · auto: {health.status}
          </span>
        )}
      </div>
      {filtered.length === 0 && effective === "On Track" && (
        <div className="text-xs text-white/60">No material issues right now.</div>
      )}
      {visible.length > 0 && (
        <ul className="text-xs text-white/75 space-y-0.5">
          {visible.map((r, i) => (
            <li key={i} className="flex items-start gap-1.5"><span className="text-white/30">·</span> <span>{r.text}</span></li>
          ))}
        </ul>
      )}
      {filtered.length > previewCount && (
        <button type="button" onClick={() => setShowAll(v => !v)} className="mt-1 text-[10px] uppercase tracking-wider text-white/50 hover:text-white">
          {showAll ? "Show less" : `Show ${filtered.length - previewCount} more`}
        </button>
      )}
      {health.override?.reason && (
        <div className="mt-2 pt-2 border-t border-white/10 text-[11px] text-white/60">
          Override reason: {health.override.reason}
        </div>
      )}
    </div>
  );
}

/** Small helper to summarise per-entity health counts. */
export function healthSummary(projects) {
  const counts = { "On Track": 0, "Needs Attention": 0, "At Risk": 0, "Overdue": 0 };
  for (const p of projects || []) {
    const s = p.health?.override?.status || p.health?.status;
    if (s && counts[s] != null) counts[s]++;
  }
  return counts;
}
